import { Bell, ChevronUp, X } from 'lucide-react'
import { Button } from 'react-aria-components'
import { RealtimeConnectionState } from '@/realtime/realtime-dispatcher'
import { type NotificationEntry, NotificationVariant } from './notification-store'

/**
 * Persistent activity status bar, fixed to the bottom of the app shell.
 *
 * Modelled on the IDE status-bar pattern rather than on toasts: it is always
 * present, shows the most recent notification until a newer one replaces it, and
 * expands into the full history. A toast answers "what just happened" and then
 * destroys the evidence; this answers it and keeps it.
 *
 * Purely presentational — every value and handler arrives as a prop, so it renders
 * from Storybook with no store, no router, and no realtime connection.
 *
 * Accessibility, which is where this pattern is usually got wrong:
 * - The bar is a labelled `region` landmark, NOT itself a live region. `role="status"`
 *   on the container would imply `aria-atomic`, re-announcing the connection chip and
 *   every control on each message. The live region wraps ONLY the message text.
 * - The history list is `role="log"` (implicitly polite, non-atomic — it announces the
 *   newly appended item and conveys an ordered sequence) and is mounted only while
 *   open, so a message is never announced twice.
 * - Severity is never carried by colour alone: each variant pairs its colour with a
 *   text label in the history and a distinct glyph in the bar.
 */

/** Bar height. 28px, not VS Code's 22px: a 24x24 hit target does not fit in 22. */
const BAR_HEIGHT_CLASS = 'h-7'

const VARIANT_DOT: Record<NotificationVariant, string> = {
	[NotificationVariant.INFO]: 'bg-ds-info',
	[NotificationVariant.SUCCESS]: 'bg-ds-success',
	[NotificationVariant.WARNING]: 'bg-ds-warning',
	[NotificationVariant.ERROR]: 'bg-ds-danger',
}

const VARIANT_LABEL: Record<NotificationVariant, string> = {
	[NotificationVariant.INFO]: 'Info',
	[NotificationVariant.SUCCESS]: 'Success',
	[NotificationVariant.WARNING]: 'Warning',
	[NotificationVariant.ERROR]: 'Error',
}

const CONNECTION_DOT: Record<RealtimeConnectionState, string> = {
	[RealtimeConnectionState.CONNECTED]: 'bg-ds-success',
	[RealtimeConnectionState.RECONNECTING]: 'bg-ds-warning animate-pulse',
	[RealtimeConnectionState.OFFLINE]: 'bg-ds-text-subtle',
}

/**
 * Connection wording avoids jargon: users read "offline" as their own network
 * being down. Only the states worth acting on carry text — a permanent
 * "everything is fine" badge is exactly what trains people to stop looking.
 */
const CONNECTION_LABEL: Record<RealtimeConnectionState, string | null> = {
	[RealtimeConnectionState.CONNECTED]: null,
	[RealtimeConnectionState.RECONNECTING]: 'Reconnecting…',
	[RealtimeConnectionState.OFFLINE]: 'Live updates paused',
}

/**
 * Coarse relative time.
 *
 * Beyond a day it switches to an absolute date: "3d ago" is useless for deciding
 * whether something is still relevant, and a session left open over a weekend
 * would otherwise accumulate a list of indistinguishable "2d ago" rows.
 *
 * @param {number} timestamp - Epoch milliseconds the notification was raised.
 * @param {number} now - Epoch milliseconds to compare against.
 * @returns {string} A short relative label such as `2m ago`, or a date once older than a day.
 */
export function relativeTime(timestamp: number, now: number): string {
	// Clock skew between the emitting server and the browser can put a timestamp in
	// the future; clamping avoids rendering a negative age.
	const seconds = Math.max(0, Math.round((now - timestamp) / 1000))
	if (seconds < 60) return 'just now'
	const minutes = Math.round(seconds / 60)
	if (minutes < 60) return `${minutes}m ago`
	const hours = Math.round(minutes / 60)
	if (hours < 24) return `${hours}h ago`
	return new Date(timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export interface StatusBarProps {
	/** History, oldest first — the same order the store retains. */
	entries: readonly NotificationEntry[]
	connectionState: RealtimeConnectionState
	/** Notifications raised since the history was last opened. */
	unreadCount: number
	expanded: boolean
	onToggleExpanded: () => void
	onDismiss: (id: string) => void
	onClearAll: () => void
	/** Injected so the component stays pure and its stories are deterministic. */
	now?: number
	/**
	 * Upload progress across every surface, `{ total, settled }`. Passed in
	 * rather than read from the store here, so this component stays purely
	 * presentational. Omit (or pass `outstanding: 0`) to render no chip.
	 */
	uploadSummary?: { total: number; settled: number; outstanding: number }
}

/** One row in the expanded history. */
function HistoryRow({
	entry,
	now,
	onDismiss,
}: {
	entry: NotificationEntry
	now: number
	onDismiss: (id: string) => void
}) {
	return (
		<li className="flex items-start gap-ds-2 border-b border-ds-border px-ds-3 py-ds-2 last:border-b-0">
			<span aria-hidden className={`mt-1 size-2 shrink-0 rounded-ds-full ${VARIANT_DOT[entry.variant]}`} />
			<span className="min-w-0 flex-1">
				<span className="block text-ds-sm text-ds-text">
					<span className="sr-only">{VARIANT_LABEL[entry.variant]}: </span>
					{entry.message}
				</span>
				<span className="text-ds-xs text-ds-text-muted tabular-nums">{relativeTime(entry.timestamp, now)}</span>
			</span>
			{entry.action ? (
				<Button
					onPress={entry.action.onPress}
					aria-label={`${entry.action.label}: ${entry.message}`}
					className="rounded-ds-sm px-ds-2 py-ds-1 text-ds-xs text-ds-primary outline-none hover:bg-ds-surface focus-visible:ring-2 focus-visible:ring-ds-ring"
				>
					{entry.action.label}
				</Button>
			) : null}
			<Button
				onPress={() => onDismiss(entry.id)}
				aria-label={`Dismiss: ${entry.message}`}
				className="grid size-6 shrink-0 place-items-center rounded-ds-sm text-ds-text-muted outline-none hover:bg-ds-surface hover:text-ds-text focus-visible:ring-2 focus-visible:ring-ds-ring"
			>
				<X aria-hidden className="size-3.5" />
			</Button>
		</li>
	)
}

export function StatusBar({
	entries,
	connectionState,
	unreadCount,
	expanded,
	onToggleExpanded,
	onDismiss,
	onClearAll,
	now = Date.now(),
	uploadSummary = { total: 0, settled: 0, outstanding: 0 },
}: StatusBarProps) {
	const latest = entries.at(-1)
	const newestFirst = [...entries].reverse()
	const connectionLabel = CONNECTION_LABEL[connectionState]
	// Gated on `outstanding`, not on `settled < total`: a permanently-failed item
	// counts toward `total` and never toward `settled`, so the latter would leave
	// the chip stuck forever.
	const isUploading = uploadSummary.outstanding > 0

	return (
		<div role="region" aria-label="Activity status" className="w-full">
			{expanded ? (
				<div className="max-h-[min(40vh,320px)] overflow-y-auto border-t border-ds-border bg-ds-surface-elevated">
					<div className="flex items-center justify-between px-ds-3 py-ds-2">
						<h2 className="text-ds-xs text-ds-text-muted uppercase">Recent activity</h2>
						{entries.length > 0 ? (
							<Button
								onPress={onClearAll}
								className="rounded-ds-sm px-ds-2 py-ds-1 text-ds-xs text-ds-text-muted outline-none hover:text-ds-text focus-visible:ring-2 focus-visible:ring-ds-ring"
							>
								Clear all
							</Button>
						) : null}
					</div>
					{/* `log` is polite and non-atomic, so only the newly appended item is
					    announced. Mounted only while open so the bar's own live region is
					    not duplicated. */}
					<ul aria-label="Recent activity" className="border-t border-ds-border" role="log">
						{newestFirst.length === 0 ? (
							<li className="px-ds-3 py-ds-4 text-ds-sm text-ds-text-muted">No recent activity.</li>
						) : (
							newestFirst.map((entry) => <HistoryRow key={entry.id} entry={entry} now={now} onDismiss={onDismiss} />)
						)}
					</ul>
				</div>
			) : null}

			<div
				className={`flex ${BAR_HEIGHT_CLASS} items-center gap-ds-2 border-t border-ds-border bg-ds-surface px-ds-2 text-ds-xs`}
			>
				{/* The live region is scoped to the message text alone. */}
				<div aria-atomic="true" aria-live="polite" className="flex min-w-0 flex-1 items-center gap-ds-2" role="status">
					{latest ? (
						<>
							<span aria-hidden className={`size-2 shrink-0 rounded-ds-full ${VARIANT_DOT[latest.variant]}`} />
							<span className="truncate text-ds-text">
								<span className="sr-only">{VARIANT_LABEL[latest.variant]}: </span>
								{latest.message}
							</span>
							<span className="hidden shrink-0 text-ds-text-muted tabular-nums sm:inline">
								{relativeTime(latest.timestamp, now)}
							</span>
						</>
					) : (
						<span className="text-ds-text-subtle">No recent activity</span>
					)}
				</div>

				{latest?.action ? (
					<Button
						onPress={latest.action.onPress}
						aria-label={`${latest.action.label}: ${latest.message}`}
						className="shrink-0 rounded-ds-sm px-ds-2 py-ds-1 text-ds-primary outline-none hover:bg-ds-surface-elevated focus-visible:ring-2 focus-visible:ring-ds-ring"
					>
						{latest.action.label}
					</Button>
				) : null}

				<Button
					onPress={onToggleExpanded}
					aria-expanded={expanded}
					aria-label={unreadCount > 0 ? `Recent activity, ${unreadCount} new` : 'Recent activity'}
					className="flex h-6 shrink-0 items-center gap-ds-1 rounded-ds-sm px-ds-2 text-ds-text-muted outline-none hover:bg-ds-surface-elevated hover:text-ds-text focus-visible:ring-2 focus-visible:ring-ds-ring"
				>
					<Bell aria-hidden className="size-3.5" />
					{unreadCount > 0 ? <span className="tabular-nums text-ds-text">{unreadCount}</span> : null}
					<ChevronUp aria-hidden className={`size-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
				</Button>

				{/*
				 * Visible on every route because this bar is mounted once in the app
				 * shell — so a user who navigates away mid-upload still sees it finish.
				 */}
				{isUploading && (
					<>
						<span aria-hidden className="h-3 w-px shrink-0 bg-ds-border" />
						<span className="flex h-6 shrink-0 items-center px-ds-1 text-ds-text-muted tabular-nums">
							Uploading {uploadSummary.settled}/{uploadSummary.total}…
						</span>
					</>
				)}

				<span aria-hidden className="h-3 w-px shrink-0 bg-ds-border" />

				{/* Not a live region: a reconnect storm would flood a screen reader. */}
				<span className="flex h-6 shrink-0 items-center gap-ds-1 px-ds-1 text-ds-text-muted">
					<span aria-hidden className={`size-2 rounded-ds-full ${CONNECTION_DOT[connectionState]}`} />
					<span className={connectionLabel ? '' : 'sr-only'}>{connectionLabel ?? 'Live updates active'}</span>
				</span>
			</div>
		</div>
	)
}
